import type { CharacterConfig } from './constants/character-registry'

// Pure character enrichment template builders. They live in shared so the
// backend and the Character Onboarding preview render exactly the same text.

export const CHARACTER_NAME_PLACEHOLDER = '{CHARACTER_NAME}'

/**
 * Cleans and normalizes user input text
 * - Trims leading/trailing whitespace
 * - Collapses multiple spaces into single space
 */
export function cleanInput(input: string): string {
  return input.trim().replace(/\s+/gu, ' ')
}

/** A callback prevents JavaScript replacement patterns in names (e.g. $&) from being interpreted. */
export function replaceCharacterName(text: string, name: string): string {
  return text.replaceAll(CHARACTER_NAME_PLACEHOLDER, () => name)
}

/**
 * NanoBanana/Gemini character generation prompt — uses character name and mentions reference images
 */
export function getNanoBananaCharacterPrompt(config: CharacterConfig, prompt: string): string {
  const { prompt: p } = config
  const name = p.characterIdentifier
  const format = replaceCharacterName(p.requiredFormat, name)

  const examplesText = p.examples
    .map((e) => {
      const output = replaceCharacterName(e.output, name)
      return `- Input: "${e.input}"\n  Output: "${output}"`
    })
    .join('\n\n')

  const allRules = [
    `ALWAYS keep "${p.stylePrefix}, ${name}" at the start`,
    ...p.rules,
    "Use the attached reference images to understand the character's exact appearance and proportions"
  ]
  const rulesText = allRules.map((r, i) => `${i + 1}. ${r}`).join('\n')

  return `You are a prompt generator for a text-to-image generation workflow ${p.introDescription}. You will be provided with reference images of the character to guide the visual output.

Your job is to take basic user input and convert it to a detailed prompt for our model. The output must always follow this exact format and style:

REQUIRED FORMAT:
${format}

EXAMPLES:
${examplesText}

RULES:
${rulesText}

User's input: ${cleanInput(prompt)}`
}

export const BACKGROUND_REFERENCE_IMAGE_INPUTS =
  '**Additional Reference Images (Mood Board):** Extra images are provided after the inputs above. These are strictly mood/vibe references — use them ONLY for lighting, color palette, textures, and general atmosphere. Do NOT copy their layout, composition, or subject matter. Analyze these images and describe their visual qualities in the **Environment Description** section.'
export const BACKGROUND_REFERENCE_ENVIRONMENT_HINT =
  '[Describe the lighting, color palette, and atmosphere based on the user prompt AND the provided mood board images. Extract specific visual qualities from the reference images.]'
export const BACKGROUND_REFERENCE_IMAGE_RULE =
  '* **Reference Images** are for ATMOSPHERE and VIBE only — never copy layout, composition, or subjects from them. Use them to inform the **Environment Description** section.'

/**
 * Background generation prompt — uses character metadata from the character config
 */
export function getBackgroundPrompt(config: CharacterConfig, prompt: string, hasReferenceImages = false): string {
  const { background } = config
  const sceneDriven = background.scaleMode === 'scene-driven'
  const shape = background.shape.replaceAll('Input 1', 'the optional scale references (or source when absent)')

  const referenceImageInputs = hasReferenceImages ? `\n${BACKGROUND_REFERENCE_IMAGE_INPUTS}` : ''

  const environmentDescriptionHint = hasReferenceImages
    ? BACKGROUND_REFERENCE_ENVIRONMENT_HINT
    : '[Describe the lighting, color palette, and atmosphere based on the user prompt]'

  const referenceImageRule = hasReferenceImages ? `\n    ${BACKGROUND_REFERENCE_IMAGE_RULE}` : ''

  return `You are a prompt generator for the "Nano Banana" sequential image-to-image workflow featuring ${background.characterName}. You must output your response strictly in the provided Markdown format. Do not include conversational text or explanations outside the requested structure.

Use the following image roles when those inputs are supplied. There may be zero, one, or several scale references. The generation request supplies the actual attached-image role map; do not assume image positions or invent missing images.
${
  sceneDriven
    ? '**Scale references (optional):** Optional scale references guide normal physical size and proportions when useful comparisons are visible, not an absolute physical size when an explicit user request changes it.'
    : '**Scale references (optional):** Use their anatomy and physical size cues when available. Without usable visual size cues, the default character height is approximately 8 inches.'
}
**Character source (when supplied):** The visual anchor. It determines the Pose, Composition, Screen Position, and Camera Angle.${referenceImageInputs}

### Core Requirements

**Scale priority:** Explicit user size instructions override visual scale references; visual scale references override default scale. If no scale images or usable measurements/comparisons are supplied, use the existing scale mode and scene context. Never invent image-derived measurements. Default camera and environment suggestions below apply only when consistent with the resolved scale. The enrichment model must defer visual size inference to image generation.

**1. Primary Objective**
Generate a prompt that describes ${background.characterName} visible in **character source**, ${sceneDriven ? 'preserving the identity and anatomical proportions found in **scale references**, while allowing explicitly requested groups, expressions, and body deformation.' : 'but strictly applies the anatomical proportions and volume found in **scale references**.'}

**2. Scale & Environment Strategy (Crucial)**
${
  sceneDriven
    ? `* **Character Scale:** Use visible physical size comparisons from scale references by default. Honor explicit size requests and relative scale changes; infer scale from the scene and character source only when usable scale comparisons are absent.
* **Environment Scale:** Describe surroundings and object sizes consistently with the resolved character scale and requested scene. Visual comparisons establish normal size without imposing an absolute measurement on explicit size changes.
* **Relative Perspective:** Choose natural perspective and depth of field for the scene. Keep the source composition by default; adjust character size, framing, or position when explicitly requested.`
    : `* **Character Scale:** Explicit user size requests take priority. Otherwise use physical size cues from the optional scale references; only when those cues are absent, default to approximately 8 inches tall.
* **Environment Scale:** The environment is **Real-Life Sized** (1:1 scale). Do **not** describe background objects as "miniature," "toy versions," or "dollhouse" items unless explicitly specified in the user input.
* **Relative Perspective:** When the resolved character scale is small, the prompt must describe standard objects (cups, pencils, tables) as looming, large, or encompassing relative to the character.`
}

**3. Input Mapping Rules**
* **Scale references (when supplied):** Analyze this image for body shape. ${sceneDriven ? 'Preserve identity and default proportions, allowing only explicitly requested body deformation.' : 'Use these proportions with the source pose when references are present; otherwise preserve source anatomy.'}
* **Character source (when supplied):** ${sceneDriven ? 'Preserve pose, character count, composition, and camera angle unless the user explicitly requests a change. A scale request may change pixel bounds.' : 'Preserve framing and pose by default; explicit size requests and visual scale cues may change pixel bounds.'}

### Prompt Construction Guidelines

**Part 1: Character (Derived from scale references, Posed by the character source)**
* **Texture:** ${background.texture}
* **Body Shape:** ${shape}

${
  sceneDriven
    ? `**Part 2: Environment (Scene Perspective)**
* **Surface Logic:** Ground the character naturally within the requested scene, with appropriate contact shadows and lighting.
* **Depth of Field:** Choose focus, lens, and background detail appropriate to the scene and user request.`
    : `**Part 2: Environment (Macro Perspective)**
* **Surface Logic:** Place the character "on a surface" (e.g., wooden tabletop, granite counter, carpet), never generally "in a room."
* **Depth of Field:** Apply "Macro Photography" logic. The background must be soft (heavy bokeh) to emphasize the small scale established by the character's size.`
}

### Output Markdown Format
You must use the following structure exactly. Do not alter the headers or bullet keys.

### Task
Place ${background.characterName} from the character source into a generated environment; Use optional scale references for normal physical size and anatomy; explicit user size requests take priority.

### Character
* **Visual Anchor:** ${sceneDriven ? 'Match the character source composition by default; honor explicit requests to change size, framing, position, count, or expression.' : 'Preserve source composition by default; honor explicit size changes and reference size cues.'}
* **Preserve Details:** [List the specific textures and anatomy from scale references to preserve]

### Background
* **Layer Type:** ${sceneDriven ? 'Scene Backdrop' : 'Macro Backdrop'}
* **Scale Logic:** ${sceneDriven ? '[Describe consistent character and object sizes for the requested scene]' : '[Describe object sizes consistently with the resolved character size]'}
* **Environment Description:** ${environmentDescriptionHint}

### Camera
${sceneDriven ? 'Match the character source framing and camera angle by default; honor explicit camera or scale changes; use a lens and depth of field appropriate to the scene.' : 'Preserve source framing by default; honor explicit size and camera requests. Use a macro lens and shallow depth of field only when appropriate to the resolved character size.'}

### General Rules
${sceneDriven ? '* **Identity:** Preserve the reference textures, facial features, and character-specific anatomy rules above.' : '* **Prohibited Phrases:** "Wide angle," "Far away," "Walking towards," "In the distance," "Miniature furniture."'}
* **Mandatory Logic:**
    * Look at **scale references** for ${sceneDriven ? 'SHAPE, ANATOMY, and normal physical SIZE' : 'SHAPE & SIZE'}.
    * Look at **character source** for ${sceneDriven ? 'default COMPOSITION, POSE, and SCREEN POSITION' : 'COMPOSITION and SCREEN POSITION only'}.
    * Look at user text inputs to understand the scene context.
    * Combine them into the final Markdown format.
    * **${sceneDriven ? 'Preserve character identity. Allow lighting integration and explicitly requested scale, expression, group, or body deformation changes; always obey character-specific anatomy restrictions.' : 'Preserve identity and anatomy while honoring explicit size requests.'}**${referenceImageRule}

Here is the user text input prompt: ${cleanInput(prompt)}`
}
